import React, { useEffect, useRef, useState } from 'react';
import { addWeightMeasurement } from '../../api/dietApi';
import type { WeightMeasurement } from '../../api/dietApi';
import { toast } from '../ui/Toast';

interface AddMeasurementModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Викликається після успішного збереження з новим виміром */
  onSaved: (measurement: WeightMeasurement) => void;
}

/**
 * Bottom sheet «+ Dodaj pomiar» — точна копія прототипу.
 * Заголовок: "Ile ważysz dzisiaj?"
 * Підзаголовок: "Wykres zaktualizuje się od razu."
 * Поле: числове, inputmode=decimal, крок 0.1, валідація 25–400.
 * НЕ повідомляємо що лікар побачить цей вимір.
 */
export const AddMeasurementModal: React.FC<AddMeasurementModalProps> = ({
  isOpen,
  onClose,
  onSaved,
}) => {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setValue('');
      setError('');
      // Фокус на поле вводу після анімації
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [isOpen]);

  const handleSave = async () => {
    const raw = value.replace(',', '.');
    const v = parseFloat(raw);
    if (isNaN(v) || v < 25 || v > 400) {
      setError('Wpisz wagę między 25 a 400 kg');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await addWeightMeasurement(v);
      toast('Pomiar zapisany');
      onSaved(res.measurement);
      onClose();
    } catch {
      setError('Wystąpił błąd. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') onClose();
  };

  const STYLES = `
    .wm-veil { position:fixed; inset:0; background:rgba(23,16,43,.5); z-index:1000;
      opacity:0; pointer-events:none; transition:.2s; }
    .wm-veil.wm-on { opacity:1; pointer-events:auto; }
    .wm-sheet { position:fixed; left:0; right:0; bottom:0; background:#fff;
      border-radius:28px 28px 0 0; padding:20px 20px 26px; z-index:1001;
      transform:translateY(100%); transition:.25s cubic-bezier(.3,.8,.4,1); max-width:480px; margin:0 auto; }
    .wm-sheet.wm-on { transform:translateY(0); }
    .wm-grab { width:38px; height:4px; border-radius:100px; background:#DCD5EC; margin:0 auto 16px; }
    .wm-title { font-size:20px; font-weight:700; color:#361777; margin-bottom:8px; }
    .wm-sub { font-size:14px; line-height:140%; font-weight:500; color:#57437E; margin-bottom:16px; }
    .wm-input { width:100%; height:60px; border:1px solid #D6C1F1; border-radius:16px;
      padding:0 16px; font-family:inherit; font-size:22px; font-weight:600; color:#361777;
      text-align:center; outline:none; transition:border-color .15s; box-sizing:border-box; }
    .wm-input:focus { border-color:#361777; }
    .wm-input::-webkit-outer-spin-button, .wm-input::-webkit-inner-spin-button { -webkit-appearance:none; }
    .wm-err { font-size:12px; color:#c0392b; margin-top:6px; text-align:center; min-height:18px; }
    .wm-btn-primary { display:flex; align-items:center; justify-content:center; width:100%; height:54px;
      border-radius:100px; font-size:16px; font-weight:500; margin-top:16px; background:#361777;
      color:#fff; border:none; cursor:pointer; font-family:inherit; transition:.15s; }
    .wm-btn-primary:active { transform:scale(.99); }
    .wm-btn-primary:disabled { opacity:.5; cursor:default; }
    .wm-btn-out { display:flex; align-items:center; justify-content:center; width:100%; height:44px;
      border-radius:100px; font-size:14px; font-weight:500; margin-top:12px; background:none;
      color:#361777; border:1px solid #D6C1F1; cursor:pointer; font-family:inherit; transition:.15s; }
    .wm-btn-out:active { transform:scale(.99); }
  `;

  return (
    <>
      <style>{STYLES}</style>
      <div className={`wm-veil ${isOpen ? 'wm-on' : ''}`} onClick={onClose} />
      <div className={`wm-sheet ${isOpen ? 'wm-on' : ''}`}>
        <div className="wm-grab" />
        <div className="wm-title">Ile ważysz dzisiaj?</div>
        <p className="wm-sub">Wykres zaktualizuje się od razu.</p>
        <input
          ref={inputRef}
          className="wm-input"
          type="number"
          step="0.1"
          inputMode="decimal"
          placeholder="0,0"
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <div className="wm-err">{error}</div>
        <button className="wm-btn-primary" onClick={handleSave} disabled={saving || !value}>
          {saving ? 'Zapisywanie...' : 'Zapisz pomiar'}
        </button>
        <button className="wm-btn-out" onClick={onClose}>
          Anuluj
        </button>
      </div>
    </>
  );
};
